import { useCallback, useEffect, useMemo, useState } from 'react';
import { ServiceItem } from 'orderme-api-integration-client';
import { useParams } from 'react-router';
import { ServiceClient } from '../helpers/client';
import { useRouteNavigator } from '../routes/useRouteNavigator';
import useGetParameter from './useGetParameter';
import { RouterParam } from '../helpers/routerParam';
import { useAppSelector } from '../redux/hooks';
import { RootState } from '../redux/store';
import { generateSlug } from '../helpers/slugGenerator';
import { getUserId } from '../helpers/customerHelper';
import { updateDocumentTitle } from '../helpers/documentHelper';
import { trackersRunner } from '../integrations/trackersRunner';
import { RouteNavigatorService } from '../routes/routeNavigatorService';

export type ServiceItemType = 'goods' | 'credits';

const linksSelectors: Record<ServiceItemType, (state: RootState) => { id: string; dataId: string }[]> = {
  goods: (state) => state.linksState.physicalItems,
  credits: (state) => state.linksState.serviceItems,
};

const navigationHandlers: Record<ServiceItemType, (navigator: RouteNavigatorService, service: ServiceItem) => void> = {
  goods: (navigator, service) => navigator.goToGoodsItem(service),
  credits: (navigator, service) => navigator.goToCreditsItem(service),
};

export function useServiceItem(type: ServiceItemType) {
  const routeNavigator = useRouteNavigator();
  const serviceClient = useMemo(() => new ServiceClient(), []);
  const params = useParams<'name'>();
  const idParam = useGetParameter(RouterParam.Id);
  const [serviceItemId, setServiceItemId] = useState<string>();
  const [service, setService] = useState<ServiceItem>();
  const links = useAppSelector(linksSelectors[type]);
  const navigateToItem = navigationHandlers[type];

  useEffect(() => {
    const link = links.find((link) => link.id === idParam);
    if (link) {
      setServiceItemId(link.dataId);
    } else {
      routeNavigator.goHome();
    }
  }, [idParam, links, routeNavigator]);

  const getServiceItem = useCallback(
    async (id: string) => {
      try {
        setService(await serviceClient.getServiceById(id, getUserId()));
      } catch (error) {
        console.error(`Error fetching service item by ID=${id}:`, error);
        setService(null);
        routeNavigator.goHome();
      }
    },
    [routeNavigator, serviceClient]
  );

  useEffect(() => {
    if (serviceItemId) {
      getServiceItem(serviceItemId);
    } else {
      setService(null);
    }
  }, [serviceItemId, getServiceItem]);

  useEffect(() => {
    if (service) {
      updateDocumentTitle(service.name);
      if (params.name !== generateSlug(service.name)) {
        navigateToItem(routeNavigator, service);
      }
      trackersRunner.viewedProduct(service, window.location.href);
    }
  }, [navigateToItem, params.name, routeNavigator, service]);

  return service;
}
